import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import styles from './AnimatedSwitcher.module.css';

/**
 * Замена `AnimatedSwitcher` из Flutter.
 *
 * Во Flutter смену ребёнка ловит сам виджет — по ключу и типу. В React
 * элемент пересоздаётся на каждом рендере, поэтому «тот же ребёнок или уже
 * другой» решает проп [AnimatedSwitcherProps.switchKey]: это ровно тот
 * `ValueKey`, который в Dart-коде стоит на детях свитчера.
 *
 * Уходящий ребёнок остаётся в стопке до конца анимации — так делает Flutter.
 * Его последний отрисованный вид приходится запоминать в ref: к моменту
 * смены ключа проп `children` уже новый.
 */

/** `duration = const Duration(milliseconds: 400)` */
export const ANIMATED_SWITCHER_DURATION_MS = 400;

interface SwitcherEntry {
  readonly id: number;
  readonly node: ReactNode;
  /** Момент начала угасания — по нему считается, когда убрать ребёнка. */
  readonly startedAt: number;
}

interface SwitcherState {
  readonly currentKey: string | number;
  readonly currentId: number;
  /**
   * Первый ребёнок появляется без анимации — в Dart это
   * `_addEntryForNewChild(animate: false)` в `initState`.
   */
  readonly animateIn: boolean;
  readonly previous: readonly SwitcherEntry[];
  readonly nextId: number;
}

export interface AnimatedSwitcherProps {
  /** Ключ текущего ребёнка: сменился — значит, пора переключаться. */
  readonly switchKey: string | number;
  /** Длительность перехода, мс. */
  readonly duration?: number;
  /** CSS-кривая вместо `switchInCurve` (во Flutter по умолчанию линейная). */
  readonly switchInCurve?: string;
  readonly switchOutCurve?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

function switchTo(
  state: SwitcherState,
  switchKey: string | number,
  outgoing: ReactNode,
): SwitcherState {
  return {
    currentKey: switchKey,
    currentId: state.nextId,
    animateIn: true,
    previous: [
      ...state.previous,
      { id: state.currentId, node: outgoing, startedAt: performance.now() },
    ],
    nextId: state.nextId + 1,
  };
}

function useSwitcherState(
  switchKey: string | number,
  children: ReactNode,
  duration: number,
): SwitcherState {
  const [state, setState] = useState<SwitcherState>(() => ({
    currentKey: switchKey,
    currentId: 0,
    animateIn: false,
    previous: [],
    nextId: 1,
  }));
  const lastCommittedNode = useRef<ReactNode>(children);

  useEffect(() => {
    // Снимок того, что реально отрисовано: он и уедет при смене ключа.
    lastCommittedNode.current = children;
  });

  const { previous } = state;
  useEffect(() => {
    if (previous.length === 0) return undefined;
    // Flutter убирает ребёнка ровно по концу его анимации, поэтому срок
    // считается от начала угасания: следующее переключение не продлевает
    // жизнь тем, кто уже догорает (иначе стопка держала бы их размер).
    const timers = previous.map((entry) => {
      const left = entry.startedAt + duration - performance.now();
      return window.setTimeout(() => {
        setState((current) => ({
          ...current,
          previous: current.previous.filter((item) => item.id !== entry.id),
        }));
      }, Math.max(0, left));
    });
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [previous, duration]);

  if (state.currentKey !== switchKey) {
    // Обновление прямо в рендере: React перерисует сразу, без лишнего кадра.
    setState(switchTo(state, switchKey, lastCommittedNode.current));
  }

  return state;
}

export function AnimatedSwitcher({
  switchKey,
  duration = ANIMATED_SWITCHER_DURATION_MS,
  switchInCurve = 'linear',
  switchOutCurve = 'linear',
  className,
  children,
}: AnimatedSwitcherProps) {
  const state = useSwitcherState(switchKey, children, duration);
  const style = {
    '--switcher-duration': `${duration}ms`,
    '--switch-in-curve': switchInCurve,
    '--switch-out-curve': switchOutCurve,
  } as CSSProperties;

  return (
    <div className={`${styles.switcher} ${className ?? ''}`} style={style}>
      {state.previous.map((entry) => (
        <div key={entry.id} className={`${styles.entry} ${styles.previous}`}>
          {entry.node}
        </div>
      ))}
      <div
        key={state.currentId}
        className={`${styles.entry} ${state.animateIn ? styles.incoming : ''}`}
      >
        {children}
      </div>
    </div>
  );
}
